// What a caller SENDS to a league's chat (BACKLOG E7, screens/CHAT.md), and the
// two small rules every client of it needs. PLAN-2026-09-26-chat C1.
//
// Views live in `views.ts` (`ChatMessage`, `PulseItem`); these are requests,
// the split `trades.ts` makes. THE AUTHOR IS NOT IN ANY BODY: the path names
// the league and the session names who is talking.

import { z } from 'zod';
import { CHAT_EMOJI } from '../chat/emoji';
import { StickerIdSchema } from '../chat/stickers';
import { pageOf } from './envelope';
import { ChatMessage, type PulseItem } from './views';

/** CHAT.md: "A single-line input … 300 characters". */
export const CHAT_BODY_MAX = 300;

/**
 * The five quick reactions (CHAT.md, I2): the ones a message's bar offers
 * first, a tap each. Every one is in the site's emoji (`CHAT_EMOJI`).
 */
export const CHAT_REACTIONS = ['👍', '🔥', '😂', '😭', '💀'] as const;

/**
 * A reaction: any emoji of the site's set (PLAN-2026-10-27-more-fun-in-chat
 * C1), which the picker offers. Still a closed list: an emoji nobody can pick
 * to add is one nobody can pick to take back either.
 */
export const ChatReaction = z.enum(CHAT_EMOJI);
export type ChatReaction = z.infer<typeof ChatReaction>;

/**
 * How many different reactions one message holds, Discord's number, so a row
 * of them stays a row. Another of one already there is always allowed.
 */
export const CHAT_REACTION_KINDS_MAX = 20;

/**
 * The channels a league has. One (CHAT.md "One channel, everywhere"): a
 * matchup's trash talk is the league channel with the matchup attached, not a
 * second channel to keep in sync. The path keeps the segment so a direct
 * message (P1) arrives as a channel rather than as a new route.
 */
export const CHAT_CHANNELS = ['league'] as const;
export const ChatChannel = z.enum(CHAT_CHANNELS);
export type ChatChannel = z.infer<typeof ChatChannel>;

/**
 * A direct message's channel (scenario I8, PLAN-2026-10-18 §10.5): opaque,
 * `dm:` and a random hex, never anybody's id. Only its two people can read or
 * write it, and nobody else learns it exists.
 */
const DM_CHANNEL = /^dm:[0-9a-f]{24,64}$/;
export function isDmChannel(channel: string): boolean {
  return DM_CHANNEL.test(channel);
}

/** One of my direct conversations in a league, as the drawer lists it. */
export const ConversationRef = z.object({
  /** The channel to read and write (`dm:…`). */
  channel: z.string(),
  /**
   * Who it is with: their name, and the team they run in this league while
   * they run one (null once they have left).
   */
  with: z.object({
    name: z.string(),
    teamId: z.string().nullable(),
    teamName: z.string().nullable(),
  }),
  /** The newest message, masked as the reader chooses (B-448); null in a new one. */
  last: z.object({ at: z.string(), body: z.string(), mine: z.boolean() }).nullable(),
});
export type ConversationRef = z.infer<typeof ConversationRef>;

/** `POST /leagues/:id/chat/dms` — a conversation with whoever manages that team. */
export const StartDmRequest = z.object({ teamId: z.string().min(1) });
export type StartDmRequest = z.infer<typeof StartDmRequest>;

/**
 * A player or a game a line carries (PLAN-2026-10-31-players-and-games-in-chat
 * §3), by its id. The server draws the card from its own rows: a player as he
 * is when shared, a game with its score then.
 */
export const ChatShare = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('player'), playerId: z.string().min(1).max(200) }),
  z.object({ kind: z.literal('matchup'), matchupId: z.string().min(1).max(200) }),
]);
export type ChatShare = z.infer<typeof ChatShare>;

/**
 * `POST /leagues/:id/chat/:channel` — say something. Trimmed before it is measured.
 *
 * WORDS, A STICKER OR A SHARE (PLAN-2026-10-30-stickers §4,
 * PLAN-2026-10-31-players-and-games-in-chat §3): `body`; `sticker`, a
 * sticker's id, which the server draws with the writer's team, alone; or
 * `share`, a player or a game, with words or without. None of them is *Say
 * something first.*, as an empty body always was.
 *
 * `replyTo` answers a line of the same channel (PLAN-2026-10-27-more-fun-in-chat
 * C2), drawn quoted over the reply. Whom a message mentions is not sent: the
 * server finds every `@Name` of a member in `body` (`mentionedIn`), so a name
 * typed out mentions as one picked does.
 */
export const ChatPostRequest = z
  .object({
    body: z
      .string()
      .trim()
      .min(1, 'Say something first.')
      .max(CHAT_BODY_MAX, `Keep it to ${CHAT_BODY_MAX} characters.`)
      .optional(),
    sticker: StickerIdSchema.optional(),
    /** A player or a game the line carries. Added 2026-10-31. */
    share: ChatShare.optional(),
    replyTo: z.string().min(1).max(300).optional(),
  })
  .superRefine((r, ctx) => {
    if (r.body === undefined && r.sticker === undefined && r.share === undefined)
      ctx.addIssue({ code: 'custom', path: ['body'], message: 'Say something first.' });
    if (r.sticker !== undefined && (r.body !== undefined || r.share !== undefined))
      ctx.addIssue({
        code: 'custom',
        path: ['sticker'],
        message: 'Send a sticker on its own.',
      });
  })
  // THE RULE IN THE PUBLISHED SCHEMA TOO, which a refinement does not reach:
  // a client built from the schema alone knows one is required, and that a
  // sticker goes alone.
  .meta({
    anyOf: [{ required: ['body'] }, { required: ['sticker'] }, { required: ['share'] }],
    not: { anyOf: [{ required: ['sticker', 'body'] }, { required: ['sticker', 'share'] }] },
  });
export type ChatPostRequest = z.infer<typeof ChatPostRequest>;

// ── Polls (PLAN-2026-10-30-polls-in-chat, scenario I4) ─────────────────────

/** A poll's question, at most. */
export const POLL_QUESTION_MAX = 140;
/** An option's words, at most. */
export const POLL_OPTION_MAX = 60;
/** Options a poll has, at least and at most. */
export const POLL_OPTIONS_MIN = 2;
export const POLL_OPTIONS_MAX = 6;

/** How long a poll runs: a day, three days or a week. */
export const POLL_RUNS = ['day', 'three-days', 'week'] as const;
export const PollRuns = z.enum(POLL_RUNS);
export type PollRuns = z.infer<typeof PollRuns>;

/** Each run's length in hours. */
export const POLL_RUN_HOURS: Record<PollRuns, number> = { day: 24, 'three-days': 72, week: 168 };

/**
 * `POST /leagues/:id/polls` — ask the league something, in its channel: a
 * question and two to six options, no two the same, and how long it runs.
 */
export const CreatePollRequest = z
  .object({
    question: z
      .string()
      .trim()
      .min(1, 'Ask something first.')
      .max(POLL_QUESTION_MAX, `Keep the question to ${POLL_QUESTION_MAX} characters.`),
    options: z
      .array(
        z
          .string()
          .trim()
          .min(1, 'Fill in every option, or remove it.')
          .max(POLL_OPTION_MAX, `Keep each option to ${POLL_OPTION_MAX} characters.`),
      )
      .min(POLL_OPTIONS_MIN, `Give it at least ${POLL_OPTIONS_MIN} options.`)
      .max(POLL_OPTIONS_MAX, `${POLL_OPTIONS_MAX} options at most.`),
    runs: PollRuns.default('day'),
  })
  .superRefine((r, ctx) => {
    const seen = new Set<string>();
    r.options.forEach((o, i) => {
      const key = o.toLowerCase();
      if (seen.has(key))
        ctx.addIssue({
          code: 'custom',
          path: ['options', i],
          message: 'Two options say the same.',
        });
      seen.add(key);
    });
  });
export type CreatePollRequest = z.infer<typeof CreatePollRequest>;
export type CreatePollInput = z.input<typeof CreatePollRequest>;

/**
 * `POST /leagues/:id/polls/:pollId/vote` — my vote, by its option's place, or
 * null to take it back. A set, as a reaction is: a retry lands the same.
 */
export const PollVoteRequest = z.object({ option: z.number().int().min(0).nullable() });
export type PollVoteRequest = z.infer<typeof PollVoteRequest>;

/** A poll in words, a message's `body`: what a quote, a preview and the pulse read. */
export function pollSaid(question: string): string {
  return `Poll: ${question}`;
}

/**
 * `POST /leagues/:id/chat/:channel/:messageId/react` — my reaction on, or off.
 *
 * A SET, NOT A TOGGLE. A phone that lost the answer and sends a toggle again
 * takes back the reaction it just made; `on` says what should be true, so the
 * retry is the same request with the same outcome.
 */
export const ChatReactRequest = z.object({
  emoji: ChatReaction,
  on: z.boolean(),
});
export type ChatReactRequest = z.infer<typeof ChatReactRequest>;

/**
 * `POST /leagues/:id/chat/league/:messageId/hide` — the commissioner hides a
 * message, or shows it again (scenario I9, B-449). A set, as a reaction is.
 */
export const ChatHideRequest = z.object({ hidden: z.boolean() });
export type ChatHideRequest = z.infer<typeof ChatHideRequest>;

/**
 * `GET /leagues/:id/chat/:channel?cursor=&limit=&since=` — newest first, a
 * page at a time; or, with `since`, only what changed after an answer's `mark`.
 * A cursor and a mark together are refused: one walks back, the other forward.
 */
export const ChatQuery = z.object({
  cursor: z.string().nullable().default(null),
  limit: z.number().int().min(1).max(200).default(100),
  since: z.string().nullable().default(null),
});
export type ChatQuery = z.infer<typeof ChatQuery>;

/**
 * One page of a channel, NEWEST FIRST: the first page is what somebody opening
 * chat wants, and `nextCursor` walks back in time. A client draws them oldest
 * at the top. `total` is not counted.
 *
 * ASKED AGAIN, IT SAYS ONLY WHAT CHANGED (2026-10-10). The newest page and
 * every `since` answer carry `mark`, how far they saw; an older page, read by
 * its cursor, carries neither it nor `merge`. Sent back as `?since=`, the answer is the messages
 * written or changed since then — a new one, or one whose reactions moved —
 * with `merge` true, to lay over what the client holds by id. When more
 * changed than `limit`, it is the newest page with `merge` false, to replace
 * what is held. A message removed outright (an achievement's line, when its
 * finder closes the account) is in no `since` answer; it leaves a client's copy
 * when it next reads the page whole. Optional because they are additive (a
 * server before 2026-10-10 sends neither, and a client asks for whole pages).
 */
export const ChatPage = pageOf(ChatMessage).extend({
  /** How far this answer saw. Opaque, and bound to its channel. */
  mark: z.string().optional(),
  /** True: `items` are only what changed since the mark. False or absent: the page whole. */
  merge: z.boolean().optional(),
  /**
   * On the league channel's answers: the reader's direct conversations in
   * the league, newest first (I8), so the chip's poll asks nothing more.
   * Absent on a conversation's own answers.
   */
  conversations: z.array(ConversationRef).optional(),
  /**
   * On the league channel's newest page read whole (not a `merge`): whom the
   * reader can begin a conversation with — each other team's manager, by
   * team, for `POST /leagues/:id/chat/dms`. A bot's seat, an open one and one
   * left are not here (I8).
   */
  people: z
    .array(z.object({ teamId: z.string(), teamName: z.string(), name: z.string() }))
    .optional(),
});
export type ChatPage = z.infer<typeof ChatPage>;

/**
 * The team color that reads on a page: the brighter of primary and secondary.
 * An author's name is drawn in it (CHAT.md "name in the team's readable
 * color"), so every client that draws a message would otherwise pick its own.
 */
export function readableColor(colors: { primary: string; secondary: string }): string {
  const lum = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  return lum(colors.primary) >= lum(colors.secondary) ? colors.primary : colors.secondary;
}

/** The grey a message with no team color is drawn in on Home's pulse. */
const NO_COLOR = '#a0a0a0';

/**
 * A message as one row of Home's League pulse (CHAT.md "Home"): an award card
 * as its first winner, a champion as the trophy, any other system post as a
 * line of the record, and talk as talk. The simulator's Home and a real
 * league's say the same thing about the same message.
 */
export function pulseItemOf(m: ChatMessage): PulseItem {
  if (m.kind === 'system' && m.card?.kind === 'awards' && m.card.items.length) {
    const first = m.card.items[0]!;
    const more = m.card.items.length - 1;
    return {
      kind: 'award',
      id: m.id,
      title: m.card.title,
      team: first.team,
      detail: `${first.name}: ${first.team.name}${first.valueLabel ? `, ${first.valueLabel}` : ''}${more > 0 ? ` · ${more} more` : ''}`,
      at: m.at,
    };
  }
  if (m.kind === 'system' && m.card?.kind === 'champion')
    return {
      kind: 'award',
      id: m.id,
      title: m.card.trophyName,
      team: m.card.team,
      detail: m.card.line,
      at: m.at,
    };
  if (m.kind === 'system') return { kind: 'transaction', id: m.id, summary: m.body, at: m.at };
  return {
    kind: 'chat',
    id: m.id,
    author: m.author,
    authorColor: m.authorColor ?? NO_COLOR,
    body: m.body,
    at: m.at,
    ...(m.kind === 'ai' ? { bot: true as const } : {}),
  };
}
